import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Wallet,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Download,
  X,
  AlertTriangle,
  Lock,
  TrendingUp,
  CheckCircle,
  Building2,
  FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelBudgets = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editBudget, setEditBudget] = useState(null);
  const [deleteBudget, setDeleteBudget] = useState(null);

  // Form State
  const initialForm = {
    financial_year: 'FY 2026-27',
    company: 'Acme Corp',
    branch: 'Bengaluru HQ',
    department: 'Engineering & Product',
    project: 'N/A',
    category: 'Department',
    name: 'Engineering & Product',
    budget_amount: 1500000,
    monthly_allocation: 125000,
    consumed: 0,
    budget_owner: 'CTO / VP Engg',
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/budgets`, { withCredentials: true });
      setBudgets(data.budgets || []);
    } catch (err) {
      toast.error('Failed to load travel budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.budget_amount) {
      toast.error('Please enter allocation name and budget amount');
      return;
    }

    try {
      if (editBudget) {
        await axios.put(`${API_URL}/travel-desk/budgets/${editBudget._id}`, formData, { withCredentials: true });
        toast.success('Travel budget updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/budgets`, formData, { withCredentials: true });
        toast.success('New Travel budget allocated successfully!');
      }
      setShowCreateModal(false);
      setEditBudget(null);
      setFormData(initialForm);
      fetchBudgets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save budget');
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/budgets/${budgetId}`, { withCredentials: true });
      toast.success('Budget allocation deleted');
      setDeleteBudget(null);
      fetchBudgets();
    } catch (err) {
      toast.error('Failed to delete budget allocation');
    }
  };

  const openEdit = (b) => {
    setEditBudget(b);
    setFormData({
      financial_year: b.financial_year || 'FY 2026-27',
      company: b.company || 'Acme Corp',
      branch: b.branch || 'Bengaluru HQ',
      department: b.department || 'General',
      project: b.project || 'N/A',
      category: b.category || 'Department',
      name: b.name || b.department || b.project || 'Budget Allocation',
      budget_amount: b.budget_amount || 0,
      monthly_allocation: b.monthly_allocation || 0,
      consumed: b.consumed || 0,
      budget_owner: b.budget_owner || '',
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Financial Year', 'Category', 'Budget Name', 'Department/Project', 'Allocated Amount', 'Consumed', 'Remaining', 'Utilization %', 'Owner'];
    const rows = filteredBudgets.map(b => {
      const pct = Math.round(((b.consumed || 0) / (b.budget_amount || 1)) * 100);
      return [
        b.financial_year, b.category, `"${b.name}"`, b.department || b.project,
        b.budget_amount, b.consumed, b.remaining, `${pct}%`, b.budget_owner
      ];
    });
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Travel_Budgets_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredBudgets = budgets.filter((b) => {
    const q = searchTerm.toLowerCase();
    const matchCategory = categoryFilter === 'all' || b.category === categoryFilter;
    const matchQuery = (
      (b.name || '').toLowerCase().includes(q) ||
      (b.department || '').toLowerCase().includes(q) ||
      (b.project || '').toLowerCase().includes(q) ||
      (b.financial_year || '').toLowerCase().includes(q)
    );
    return matchCategory && matchQuery;
  });

  // Calculate Aggregates
  const totalAllocated = filteredBudgets.reduce((acc, b) => acc + (b.budget_amount || 0), 0);
  const totalConsumed = filteredBudgets.reduce((acc, b) => acc + (b.consumed || 0), 0);
  const totalRemaining = totalAllocated - totalConsumed;
  const overallPct = totalAllocated > 0 ? Math.round((totalConsumed / totalAllocated) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Wallet className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Department &amp; Project Travel Budgets</h1>
            <p className="text-xs text-blue-200">
              Corporate Travel Allocations, Real-Time Spend Tracking, 80% Threshold Warnings &amp; 100% Budget Locks.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.budget.edit') && (
            <button
              onClick={() => {
                setEditBudget(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Allocate Budget
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
          <div className="text-slate-400 text-xs font-bold uppercase">Total Budget Allocated</div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            ₹ {totalAllocated.toLocaleString('en-IN')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
          <div className="text-slate-400 text-xs font-bold uppercase">Total Spend Consumed</div>
          <div className="text-2xl font-black text-indigo-600 font-mono">
            ₹ {totalConsumed.toLocaleString('en-IN')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
          <div className="text-slate-400 text-xs font-bold uppercase">Remaining Balance</div>
          <div className="text-2xl font-black text-emerald-600 font-mono">
            ₹ {totalRemaining.toLocaleString('en-IN')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
          <div className="text-slate-400 text-xs font-bold uppercase">Overall Utilization</div>
          <div className="text-2xl font-black text-amber-500 font-mono">
            {overallPct}%
          </div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search budget name, department, project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold">
            <Filter className="w-3.5 h-3.5" /> Category:
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Categories</option>
            <option value="Department">Department</option>
            <option value="Project">Project</option>
          </select>

          <button
            onClick={fetchBudgets}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Budget Cards Grid */}
      {loading ? (
        <div className="p-12 text-center text-slate-500 font-medium">Loading budget allocations...</div>
      ) : filteredBudgets.length === 0 ? (
        <div className="p-12 text-center text-slate-400 font-semibold italic">
          No budget allocations found matching your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredBudgets.map((b) => {
            const allocated = b.budget_amount || 1;
            const consumed = b.consumed || 0;
            const remaining = Math.max(0, allocated - consumed);
            const pct = Math.round((consumed / allocated) * 100);

            return (
              <div key={b._id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4 relative">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-slate-400">
                      {b.category === 'Department' ? <Building2 className="w-3.5 h-3.5 text-blue-500" /> : <FolderKanban className="w-3.5 h-3.5 text-indigo-500" />}
                      <span>{b.category} Budget · {b.financial_year}</span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-base">{b.name}</h3>
                    <div className="text-[11px] text-slate-500">Owner: <b>{b.budget_owner || 'Unassigned'}</b></div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <span className={`font-mono text-xs font-bold px-2.5 py-1 rounded-lg ${
                      pct >= 100 ? 'bg-rose-100 text-rose-800' : pct >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {pct}% Consumed
                    </span>
                  </div>
                </div>

                {/* Progress Bar & Warning Checks */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-semibold text-slate-600">
                    <span>Spend Progress</span>
                    <span className="font-mono">₹ {consumed.toLocaleString('en-IN')} / ₹ {allocated.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pct >= 100 ? 'bg-rose-600' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>

                {/* Warning and Lock Alerts */}
                {pct >= 100 ? (
                  <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-bold flex items-center gap-2">
                    <Lock className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>100% BUDGET CAP REACHED — Booking Blocked!</span>
                  </div>
                ) : pct >= 80 ? (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-xs font-bold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
                    <span>80% Utilization Warning Threshold Exceeded!</span>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 text-xs">
                  <div>
                    <span className="text-slate-400 font-medium block">Consumed</span>
                    <span className="font-mono font-bold text-slate-900">₹ {consumed.toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-medium block">Remaining</span>
                    <span className="font-mono font-bold text-emerald-600">₹ {remaining.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <div className="flex justify-end gap-1.5 pt-1">
                  {hasPermission(user, 'travel_desk.budget.edit') && (
                    <button
                      onClick={() => openEdit(b)}
                      className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg text-xs font-bold"
                      title="Edit Budget"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteBudget(b)}
                    className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg text-xs font-bold"
                    title="Delete Budget"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-500" />
                {editBudget ? `Edit Budget Allocation (${editBudget.name})` : 'Allocate New Travel Budget'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveBudget} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Financial Year *</label>
                  <select
                    value={formData.financial_year}
                    onChange={(e) => setFormData({ ...formData, financial_year: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-semibold"
                  >
                    <option value="FY 2026-27">FY 2026-27</option>
                    <option value="FY 2025-26">FY 2025-26</option>
                    <option value="FY 2027-28">FY 2027-28</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-bold"
                  >
                    <option value="Department">Department</option>
                    <option value="Project">Project</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Allocation Target / Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  placeholder="Engineering & Product / Client Implementation Alpha"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Branch / Location</label>
                  <input
                    type="text"
                    value={formData.branch}
                    onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Total Budget Amount (₹) *</label>
                  <input
                    type="number"
                    required
                    value={formData.budget_amount}
                    onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold text-indigo-700"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Monthly Allocation (₹)</label>
                  <input
                    type="number"
                    value={formData.monthly_allocation}
                    onChange={(e) => setFormData({ ...formData, monthly_allocation: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Already Consumed (₹)</label>
                  <input
                    type="number"
                    value={formData.consumed}
                    onChange={(e) => setFormData({ ...formData, consumed: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Budget Owner / Manager</label>
                  <input
                    type="text"
                    value={formData.budget_owner}
                    onChange={(e) => setFormData({ ...formData, budget_owner: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="VP Engg / Project Lead"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editBudget ? 'Save Changes' : 'Save Budget Allocation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteBudget && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete budget allocation for <b>{deleteBudget.name}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleteBudget(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeleteBudget(deleteBudget._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelBudgets;
